import { usePrivy } from "@privy-io/react-auth"
import type { ConvexProviderWithAuth } from "convex/react"
import { type ComponentProps, useCallback, useMemo, useRef } from "react"
import { captureSentryException, captureSentryMessage } from "@/lib/sentry"

const ACCESS_TOKEN_TIMEOUT_MS = 8_000

type ConvexAuthHook = ReturnType<ComponentProps<typeof ConvexProviderWithAuth>["useAuth"]>

function timeoutAfter(ms: number) {
	return new Promise<null>(resolve => {
		window.setTimeout(() => resolve(null), ms)
	})
}

export function usePrivyConvexAuth(): ConvexAuthHook {
	const { ready, user, getAccessToken } = usePrivy()
	const getAccessTokenRef = useRef(getAccessToken)
	getAccessTokenRef.current = getAccessToken
	const didReportTimeoutRef = useRef(false)
	const didReportErrorRef = useRef(false)
	const userId = user?.id ?? null
	const fetchAccessToken = useCallback(async () => {
		if (!userId) {
			didReportTimeoutRef.current = false
			didReportErrorRef.current = false
			return null
		}

		try {
			const token = await Promise.race([
				getAccessTokenRef.current(),
				timeoutAfter(ACCESS_TOKEN_TIMEOUT_MS),
			])
			if (token === null && !didReportTimeoutRef.current) {
				didReportTimeoutRef.current = true
				captureSentryMessage("privy_convex_access_token_timeout", {
					extra: {
						location: `${window.location.pathname}${window.location.search}`,
						privyReady: ready,
						userId,
					},
					level: "warning",
				})
			}
			if (token !== null) {
				didReportTimeoutRef.current = false
				didReportErrorRef.current = false
			}
			return token
		} catch (error: unknown) {
			if (!didReportErrorRef.current) {
				didReportErrorRef.current = true
				captureSentryException(error, {
					extra: {
						location: `${window.location.pathname}${window.location.search}`,
						privyReady: ready,
						userId,
					},
					tags: {
						stage: "privy_convex_access_token",
					},
				})
			}
			return null
		}
	}, [ready, userId])

	return useMemo(
		() => ({
			isLoading: !ready,
			isAuthenticated: ready && !!userId,
			fetchAccessToken,
		}),
		[ready, userId, fetchAccessToken],
	)
}
